// AC-BIN-015: 時間前入札で入札制限 (テナント設定、 bid-limits.md §2-2) に違反した
// 札が提出の対象から外れたとき、 違反件数と違反行の詳細を提出確認モーダルに
// 表示する。
//
// intraday の提出 (BidCreateTab.handleSubmitToJepx) は未送信の注文を 1 件ずつ
// TransitionBiddingIntradayOrderState(SUBMIT/REQUEST) する。 backend
// (ensureWithinIntradayBidLimits, intraday_order.go) は違反した軸の設定値
// (下限価格 / 上限価格 / 上限数量(1札)) を ErrorInfo.metadata に積んで返す
// (proto `IntradayOrderWriteBlockedReason.BID_LIMIT_VIOLATION`)。 同一
// (エリア × コマ × 売買) の合計が上限数量(コマ計) を超える拒否は別の reason
// (`BID_VOLUME_TOTAL_LIMIT_VIOLATION`) で、 metadata の `firstTotalMw` /
// `firstLimitMw` に合計と上限を積む (AC-BIN-052)。 対象の札自体
// (実際の価格・数量・エリア・売買・コマ) は frontend が送信しようとした自分の
// 入力として既に持っているため、 ここでは metadata (設定側の値) と組み合わせて
// 明細行を組み立てるだけでよい。 ただし戦略モードの札は違反したのが最も早い段
// (= 札自身の価格・量) とは限らないので、 backend が違反した段の値も metadata に積む。
//
// スポット入札の違反明細 (SpotBidding/models/spotApprovalDecision.ts /
// spotBidValidation.ts) は settings「入札制限」を事前に丸ごと取得し、 承認申請
// 確認モーダルを開く前にクライアント側で判定する (= RPC 前に違反を確定できる)。
// intraday はこの提出確認モーダルの時点でまだテナント入札制限の事前取得を
// 行っておらず (JEPX 制度上限の hasIntradayLimitViolation のみ)、 違反の実体は
// 個々の SUBMIT RPC が FailedPrecondition で返すまで判明しない。 そのため本
// module はスポットと違い「RPC 失敗から明細を抽出する」 形をとる。 表示先
// (`~/components/bidding/common/SubmitConfirmModal` の `ViolationSection`) は
// スポットと共通の仕組みを流用する。
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingLifecycleAction } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import {
  IntradayOrderWriteBlockedReason,
  IntradayOrderWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { TFunction } from 'i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { readAppErrorInfo } from '~/libs/appErrorInfo'
import { convertAreaLabel } from '~/models/area'
import { formatJstTimeHm } from '~/models/dateTime'
import { decimalVolumeToMw } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'
import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import {
  type IntradaySubmitFailure,
  type IntradaySubmitFailureBid,
  classifyIntradaySubmitFailure,
  isRetryLaterFailure,
} from '~/pages/IntradayBidding/models/intradaySubmitFailure'

/**
 * 入札制限違反の軸 (backend `valueobject.BidKind` の MIN/MAX/QTY に対応)。
 * `qty-total` は上限数量(コマ計) で、 札 1 件の値でなく同一 (エリア × コマ × 売買)
 * の合計を比べる。
 */
export type IntradayBidLimitViolationKind =
  | 'price-min'
  | 'price-max'
  | 'qty-max'
  | 'qty-total'

/** 違反 1 件ぶんの明細 (= 提出確認モーダルの 1 行)。 */
export type IntradayBidLimitViolationDetail = {
  bidId: string
  kind: IntradayBidLimitViolationKind
  timeCode: number
  areaCode: AreaCode
  side: Side
  /** 実際の値 (価格違反なら 円/kWh、数量違反なら MW、 コマ計違反なら合計 MW)。 */
  actual: number
  /** 違反した軸の設定値 (下限価格 / 上限価格 / 上限数量(1札) / 上限数量(コマ計))。 */
  limit: number
}

/** metadata の MW 値 (十進文字列) を数値にする。 欠落・空・数値でない値は undefined。 */
const parseMetadataMw = (raw: string | undefined): number | undefined => {
  if (raw === undefined || raw.trim() === '') return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
}

/** 違反の明細を組むのに読む札の項目 (RPC の失敗に載らない、 札自身の値)。 */
export type IntradayViolationSourceBid = Pick<
  IntradayBidRow,
  | 'id'
  | 'timeCode'
  | 'areaCode'
  | 'direction'
  | 'priceYenPerKwh'
  | 'volumeMw'
  | 'withdrawAt'
>

/**
 * 一覧の行から、 違反明細の「実際の入札値」 に使う札の値を取る。 価格・数量のどちらかが
 * 取れない行では組まない (= 0 に潰した偽の数値を承認判断の画面に出さない)。 その場合は
 * 呼び出し元が汎用の文言に落とす。
 */
export const intradayViolationSourceBidOf = (
  order: IntradayOrderRow,
): IntradayViolationSourceBid | undefined => {
  const price = order.priceYenPerKwh?.value
  const volumeMw = decimalVolumeToMw(order.volumeMwh)
  if (price === undefined || volumeMw === undefined) return undefined
  return {
    id: order.id,
    timeCode: order.timeCode,
    areaCode: order.areaCode,
    direction: order.direction,
    priceYenPerKwh: price,
    volumeMw: String(volumeMw),
  }
}

/** AC-BIN-116: この提出操作から除外した自動取消時刻超過札。 */
export type IntradayWithdrawAtPassedDetail = Pick<
  IntradayViolationSourceBid,
  'id' | 'timeCode' | 'areaCode' | 'direction' | 'withdrawAt'
>

/**
 * 自動取消時刻を分単位で比較する。時刻を設定していない札は除外しない。
 * backend の `IsIntradayWithdrawAtPast` と同じ「現在の分も過去」規約で、
 * UI の事前案内だけに使う。実行時は backend の reason を必ず再確認する。
 */
export const isIntradayWithdrawAtPassed = (
  bid: Pick<IntradayViolationSourceBid, 'withdrawAt'>,
  now: Date,
): boolean => {
  if (bid.withdrawAt === undefined) return false
  const withdrawAt = timestampDate(bid.withdrawAt)
  return (
    Math.floor(withdrawAt.getTime() / 60_000) <=
    Math.floor(now.getTime() / 60_000)
  )
}

/** 提出確認・結果の自動取消時刻超過札の明細。 */
export const formatIntradayWithdrawAtPassedDetail = (
  bid: IntradayWithdrawAtPassedDetail,
  t: TFunction,
): string =>
  t('page:biddingIntraday.withdrawAtPassed.detail', {
    timeCode: bid.timeCode,
    area: convertAreaLabel(bid.areaCode),
    side: sideLabelOf(bid.direction, t),
    time:
      bid.withdrawAt === undefined
        ? '—'
        : formatJstTimeHm(timestampDate(bid.withdrawAt)),
  })

/** AC-BIN-116 の確認・結果表示用 Alert。 */
export const buildIntradayWithdrawAtPassedSection = (
  bids: readonly IntradayWithdrawAtPassedDetail[],
  t: TFunction,
  phase: 'beforeSubmit' | 'result',
): ViolationSection => ({
  title:
    phase === 'beforeSubmit'
      ? t('page:biddingIntraday.withdrawAtPassed.beforeSubmitTitle', {
          count: bids.length,
        })
      : t('page:biddingIntraday.withdrawAtPassed.resultTitle', {
          count: bids.length,
        }),
  body:
    phase === 'beforeSubmit'
      ? t('page:biddingIntraday.withdrawAtPassed.beforeSubmitBody')
      : undefined,
  details: bids.map((bid) => formatIntradayWithdrawAtPassedDetail(bid, t)),
})

/**
 * AC-BIN-116 承認申請: 要承認の超過札だけを件数と明細に出し、申請全体を止める
 * (#11510 の Des 判断)。
 */
export const buildIntradayRequestWithdrawAtBlockedSection = (
  bids: readonly IntradayWithdrawAtPassedDetail[],
  t: TFunction,
): ViolationSection => ({
  title: t('page:biddingIntraday.withdrawAtPassed.requestBlockedTitle', {
    count: bids.length,
  }),
  details: bids.map((bid) => formatIntradayWithdrawAtPassedDetail(bid, t)),
})

/**
 * 対象の札 1 件の提出 (SUBMIT/REQUEST) が失敗した ConnectError から、
 * AC-BIN-015 の入札制限違反明細を抽出する。 reason が
 * `BID_LIMIT_VIOLATION` / `BID_VOLUME_TOTAL_LIMIT_VIOLATION` でない
 * (= 他の理由の失敗、 楽観ロック競合等) 場合は空配列を返す
 * (= 呼び出し元の partial トーストが従来どおり扱う)。
 *
 * `BID_LIMIT_VIOLATION`: metadata に `priceMinLimit` / `priceMaxLimit` /
 * `qtyMaxLimit` のいずれかが載っていればその軸を 1 件の明細として積む
 * (backend は違反した軸だけを積む。 通常モードの札は価格軸が MIN/MAX どちらか
 * 一方で QTY は独立。 戦略モードの札は別々の段が MIN と MAX を同時に外しうるので、
 * 両方が載ることがある)。
 *
 * 実際値は、 戦略モードの札では backend が違反した段の値 (`priceMinValue` /
 * `priceMaxValue` / `qtyMaxValue`) を積むのでそれを使う。 違反したのが最も早い段
 * (= 札の最上位の価格・量) とは限らないため。 載っていなければ札自身の値を使う。
 *
 * `BID_VOLUME_TOTAL_LIMIT_VIOLATION` (AC-BIN-052): metadata の `firstTotalMw`
 * (合計) と `firstLimitMw` (上限数量(コマ計)) から `qty-total` の明細を 1 件積む。
 * どちらかが欠落・非数値なら明細を作らない。
 */
export const extractIntradayBidLimitViolationDetails = (
  err: unknown,
  order: IntradayViolationSourceBid,
): IntradayBidLimitViolationDetail[] => {
  if (!(err instanceof ConnectError)) return []
  const info = readAppErrorInfo(err, IntradayOrderWriteBlockedReasonSchema)
  if (info === undefined) return []
  const base = {
    bidId: order.id,
    timeCode: order.timeCode,
    areaCode: order.areaCode,
    side: order.direction,
  }
  if (
    info.reason ===
    IntradayOrderWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION
  ) {
    const actual = parseMetadataMw(info.metadata.firstTotalMw)
    const limit = parseMetadataMw(info.metadata.firstLimitMw)
    if (actual === undefined || limit === undefined) return []
    return [{ ...base, kind: 'qty-total', actual, limit }]
  }
  if (info.reason !== IntradayOrderWriteBlockedReason.BID_LIMIT_VIOLATION) {
    return []
  }
  const out: IntradayBidLimitViolationDetail[] = []
  if (info.metadata.priceMinLimit !== undefined) {
    out.push({
      ...base,
      kind: 'price-min',
      actual: Number(info.metadata.priceMinValue ?? order.priceYenPerKwh),
      limit: Number(info.metadata.priceMinLimit),
    })
  }
  if (info.metadata.priceMaxLimit !== undefined) {
    out.push({
      ...base,
      kind: 'price-max',
      actual: Number(info.metadata.priceMaxValue ?? order.priceYenPerKwh),
      limit: Number(info.metadata.priceMaxLimit),
    })
  }
  if (info.metadata.qtyMaxLimit !== undefined) {
    out.push({
      ...base,
      kind: 'qty-max',
      actual: Number(info.metadata.qtyMaxValue ?? order.volumeMw),
      limit: Number(info.metadata.qtyMaxLimit),
    })
  }
  return out
}

/** AC-BIN-116: 実行中に自動取消時刻を越えた直接提出札を ErrorInfo reason から拾う。 */
export const extractIntradayWithdrawAtPassedDetail = (
  err: unknown,
  order: IntradayViolationSourceBid,
): IntradayWithdrawAtPassedDetail | undefined => {
  if (!(err instanceof ConnectError)) return undefined
  const info = readAppErrorInfo(err, IntradayOrderWriteBlockedReasonSchema)
  if (
    info?.reason !== IntradayOrderWriteBlockedReason.SUBMIT_WITHDRAW_AT_PASSED
  ) {
    return undefined
  }
  return {
    id: order.id,
    timeCode: order.timeCode,
    areaCode: order.areaCode,
    direction: order.direction,
    withdrawAt: order.withdrawAt,
  }
}

/**
 * 違反した札の数 (AC-BIN-052 の見出し・本文の件数)。 1 つの札が複数の軸で違反しても
 * 1 件と数える (明細行は軸ごとに出す)。
 */
export const countViolatedBids = (
  violations: readonly IntradayBidLimitViolationDetail[],
): number => new Set(violations.map((v) => v.bidId)).size

/** 提出 1 回のうちの札 1 件ぶん: 送ったアクションと、 その RPC の成否。 */
export type IntradaySubmitAttempt = {
  bid: IntradaySubmitFailureBid
  /** SUBMIT (JEPX へ提出) か REQUEST (承認申請)。 */
  action: BiddingLifecycleAction
  result: PromiseSettledResult<unknown>
}

/** 提出 1 回の完了通知 (トースト)。 通知を出さない時は undefined。 */
export type IntradaySubmitToast =
  /** 全件成功。 */
  | { kind: 'success' }
  /**
   * 一部の札が通らなかった (AC-BIN-009 のエラーバナー)。 ok = 通った件数 (承認申請・予約も数える)。
   * 1 件以上通った時だけ。
   */
  | { kind: 'partialFailure'; ok: number; failed: number }
  /**
   * 1 件も通らず、 通らなかった札がある (AC-BIN-009)。 `retryLaterOnly` は通らなかった札が
   * すべて時間をおけば通る失敗の時 (トーストで知らせる)。 それ以外は見出しなしのエラーバナー。
   */
  | { kind: 'allFailed'; failed: number; retryLaterOnly: boolean }
  /** 直接提出の札が入札制限違反だけで外れ、 承認申請は通った (承認申請した件数)。 */
  | { kind: 'requested'; count: number }

/** 提出 1 回の結果を札ごとに分けて数えたもの。 */
export type IntradaySubmitSummary = {
  /** 提出済 (SUBMIT が成功した札 = JEPX へ提出した札)。 */
  submittedCount: number
  /** 承認申請済 (REQUEST が成功した札。 JEPX へは送っていない)。 */
  requestedCount: number
  /** 入札制限違反の明細 (軸ごと)。 */
  violations: IntradayBidLimitViolationDetail[]
  /** backend が実行時に自動取消時刻超過として外した直接提出札。 */
  withdrawAtPassed: IntradayWithdrawAtPassedDetail[]
  /**
   * 通らなかった札と理由の区分 (AC-BIN-009)。 入札制限違反・自動取消時刻の超過で提出の対象から
   * 外した札は入れない。
   */
  failures: IntradaySubmitFailure[]
  toast: IntradaySubmitToast | undefined
}

/**
 * 提出 1 回の RPC の成否を札ごとに分けて数え、 完了通知を決める (AC-BIN-015 / AC-BIN-057)。
 *
 * - 直接提出 (SUBMIT) の札が入札制限違反で外れた時は、 承認申請になる札が混ざっていても
 *   「提出 一部失敗」 の失敗に数えない。 JEPX へ提出した件数は AC-BIN-052 の表示が示す
 *   (AC-BIN-057)。 違反だけなら、 承認申請が通った札がある時だけ承認申請の件数を知らせる
 *   (承認申請と JEPX 提出の件数を分けて知らせる、 #8321)。
 * - 承認申請 (REQUEST) の札が入札制限違反で拒否された時も、 提出の対象から外した札として
 *   失敗に数えない (AC-BIN-009 は承認申請も同じ扱いで、 違反は AC-BIN-015 の表示が示す)。
 * - 違反以外の理由で失敗した札は失敗に数える。
 */
export const summarizeIntradaySubmitResults = (
  attempts: readonly IntradaySubmitAttempt[],
  isGcPassed: (bid: IntradaySubmitFailureBid) => boolean = () => false,
): IntradaySubmitSummary => {
  let submittedCount = 0
  let requestedCount = 0
  let failedCount = 0
  let rejectedCount = 0
  const violations: IntradayBidLimitViolationDetail[] = []
  const withdrawAtPassed: IntradayWithdrawAtPassedDetail[] = []
  const failures: IntradaySubmitFailure[] = []
  for (const { bid, action, result } of attempts) {
    if (result.status === 'fulfilled') {
      if (action === BiddingLifecycleAction.REQUEST) requestedCount += 1
      else submittedCount += 1
      continue
    }
    rejectedCount += 1
    const details = extractIntradayBidLimitViolationDetails(result.reason, bid)
    violations.push(...details)
    const withdrawAtPassedDetail = extractIntradayWithdrawAtPassedDetail(
      result.reason,
      bid,
    )
    if (
      withdrawAtPassedDetail !== undefined &&
      action === BiddingLifecycleAction.SUBMIT
    ) {
      withdrawAtPassed.push(withdrawAtPassedDetail)
    }
    const droppedByViolation =
      details.length > 0 ||
      (withdrawAtPassedDetail !== undefined &&
        action !== BiddingLifecycleAction.REQUEST)
    if (!droppedByViolation) {
      failedCount += 1
      failures.push({
        bid,
        kind: classifyIntradaySubmitFailure(result.reason, isGcPassed(bid)),
        error: result.reason,
      })
    }
  }
  const passedCount = submittedCount + requestedCount
  const toast: IntradaySubmitToast | undefined =
    rejectedCount === 0
      ? { kind: 'success' }
      : failedCount > 0
        ? passedCount > 0
          ? { kind: 'partialFailure', ok: passedCount, failed: failedCount }
          : {
              kind: 'allFailed',
              failed: failedCount,
              retryLaterOnly: failures.every(isRetryLaterFailure),
            }
        : requestedCount > 0
          ? { kind: 'requested', count: requestedCount }
          : undefined
  return {
    submittedCount,
    requestedCount,
    violations,
    withdrawAtPassed,
    failures,
    toast,
  }
}

const sideLabelOf = (side: Side, t: TFunction): string =>
  side === Side.BUY
    ? t('page:biddingSpot.side.buy')
    : t('page:biddingSpot.side.sell')

/** 明細行の共通見出し (spot `spotBidValidation.ts` の detailHead と同形式)。 */
const detailHeadOf = (
  v: IntradayBidLimitViolationDetail,
  t: TFunction,
): string =>
  t('page:biddingIntraday.violation.detailHead', {
    timeCode: v.timeCode,
    area: convertAreaLabel(v.areaCode),
    side: sideLabelOf(v.side, t),
  })

/** 違反 1 件を提出確認モーダル用の 1 行明細に整形する (AC-BIN-015)。 */
export const formatIntradayBidLimitViolationDetail = (
  v: IntradayBidLimitViolationDetail,
  t: TFunction,
): string => {
  const head = detailHeadOf(v, t)
  switch (v.kind) {
    case 'price-min':
      return t('page:biddingIntraday.violation.limitPriceMinDetail', {
        head,
        actual: v.actual.toFixed(2),
        min: v.limit.toFixed(2),
      })
    case 'price-max':
      return t('page:biddingIntraday.violation.limitPriceMaxDetail', {
        head,
        actual: v.actual.toFixed(2),
        max: v.limit.toFixed(2),
      })
    case 'qty-max':
      return t('page:biddingIntraday.violation.limitQtyDetail', {
        head,
        actual: v.actual,
        max: v.limit,
      })
    case 'qty-total':
      return t('page:biddingIntraday.violation.limitQtyTotalDetail', {
        head,
        actual: v.actual,
        max: v.limit,
      })
  }
}

/**
 * 違反明細の宛先。 解消手順が申請者と承認者で違う (申請者は自分で札を直せるが、
 * 承認者は直せないので差戻すしかない) ため、 body 文言だけ出し分ける。 spot の
 * `SpotViolationAudience` (`SpotBidding/models/spotApprovalDecision.ts`) と同形。
 */
export type IntradayBidLimitViolationAudience = 'submit' | 'approve'

/**
 * 違反明細一覧を `SubmitConfirmModal.violationSections` の 1 セクションに
 * 組む。 `violations` が空なら details が空のセクションを返す (呼び出し元の
 * `SubmitConfirmModal` 側で details.length === 0 のセクションは描画されない)。
 *
 * 見出し・本文の件数は違反した札の数 (`countViolatedBids`)。 明細行は軸ごとに出す。
 * 本文の「違反した N件を除き、 M件を提出しました」 は N も M も札の数で数える
 * (AC-BIN-052)。
 *
 * `sentCount` = 直近の提出試行で通った札の件数 (承認申請・予約を含む。 AC-BIN-009)。 違反した札だけが
 * 提出の対象から外れる (AC-BIN-015) ため、 1 件でも提出できていれば提出した件数と
 * 提出しなかった件数を示す本文 (`limitBodyPartial`) にし、 0 件なら
 * 「提出できません」 の本文 (`limitBody`) にする。
 *
 * `reservedOpenTime` = 市場が未開場で予約送信になった提出の開場時刻。 渡された時は、
 * 一部提出の本文を予約した件数を示す本文 (`limitBodyPartialReserved`) にする (#10765)。
 */
export const buildIntradayBidLimitViolationSection = (
  violations: readonly IntradayBidLimitViolationDetail[],
  sentCount: number,
  t: TFunction,
  audience: IntradayBidLimitViolationAudience = 'submit',
  reservedOpenTime?: string,
): ViolationSection => {
  const violatedBidCount = countViolatedBids(violations)
  return {
    title: t('page:biddingIntraday.violation.limitTitle', {
      count: violatedBidCount,
    }),
    // 解消手順の文言 key は宛先ごとに実キーを書き出す (= 動的組み立てにすると i18n key の
    // grep / 未使用検出が効かなくなる。 spot `buildSpotViolationSections` と同じ)。
    // 承認確認 (approve) は部分送信の概念が無い (承認/差戻しの二択で、 一部の札だけ
    // JEPX へ送るという状態が無い) ため、 sentCount による出し分け (T-41) は
    // audience === 'submit' の場合だけ行う。
    body:
      audience === 'approve'
        ? t('page:biddingIntraday.violation.limitBodyApprove')
        : sentCount > 0
          ? reservedOpenTime === undefined
            ? t('page:biddingIntraday.violation.limitBodyPartial', {
                count: violatedBidCount,
                sent: sentCount,
              })
            : t('page:biddingIntraday.violation.limitBodyPartialReserved', {
                count: violatedBidCount,
                sent: sentCount,
                openTime: reservedOpenTime,
              })
          : t('page:biddingIntraday.violation.limitBody'),
    details: violations.map((v) => formatIntradayBidLimitViolationDetail(v, t)),
  }
}
